import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { useTheme } from '@emotion/react';

import { PKDFilledButton } from 'components/FilledButton';
import MaskedInput from 'components/MaskedInput';
import { PKDModal } from 'components/Modal';
import { PKDSelect } from 'components/PKDSelect';
import { TextInput } from 'components/PKDTextField';
import { FormRow } from 'components/common/FormWrapper/styles';
import { AmericanStates } from 'mocks/americanStates';
import {
  getPersonContactRecords,
  initialContactFormValues,
  formatAttributeName,
  formatPhoneNumbers,
  formatNationalPhoneNumber,
} from 'pages/ContactInformation/utils';
import {
  getPersonMedicalRecords,
  getPersonAttribute,
  getAttributesPayload,
} from 'pages/MedicalInformation/utils';
import { ErrorWrapper } from 'shared-components/ErrorWrapper';
import { openSnackbar } from 'store/application';
import { getArrayOfYears, getDaysOfMonth } from 'utils/date';
import { Gender } from 'utils/enum';
import { getRandomIndex } from 'utils/random';
import {
  addressFormatRegExp,
  nameRegExp,
  phoneNumberFormatRegExp,
  zipCodeRegExp,
  apostropheRegExp,
} from 'utils/regexp';
import { useUpsertPersonAttributeMutation } from 'store/api/pkd/endpoints/person';


const allFields = [
  'firstName',
  'lastName',
  'email',
  'home_phone',
  'phone',
  'monthOfBirth',
  'dayOfBirth',
  'yearOfBirth',
  'address',
  'city',
  'state',
  'postal_code',
  'gender',
];
const PersonInformationModal = ({
  isOpenModal,
  handleClose,
  person,
  auth0User,
  personId,
  isPulseSurveyOpenModal,
  isPersonLoading,
  isButtonLoading,
  hasOptedInRef,
  handleIsOptingIn,
  states
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [upsertPersonAttribute, { isLoading: isPersonAttributeUpserting }] =
    useUpsertPersonAttributeMutation();

  const personInfo = {
    ...getPersonContactRecords(person?.attributes),
    ...getPersonMedicalRecords(person?.attributes, {
      date_of_birth : '',
      gender        : '',
      first_name    : '',
      last_name     : '',
    }),
  };

  const getDateOfBirth = (dateOfBirth) => {
    if (!dateOfBirth) {
      return {};
    }
  
    let yearOfBirth, monthOfBirth, dayOfBirth;
  
    if (dateOfBirth.includes('T')) {
      const date = new Date(dateOfBirth);
      yearOfBirth = String(date.getUTCFullYear());
      monthOfBirth = String(date.getUTCMonth() + 1).padStart(2, '0');
      dayOfBirth = String(date.getUTCDate());
    } else if (dateOfBirth.includes('/')) {
    
      const dateOfBirthSplit = dateOfBirth.split('/');
      yearOfBirth = dateOfBirthSplit[0];
      monthOfBirth = dateOfBirthSplit[1];
      dayOfBirth = dateOfBirthSplit[2];
    }
  
    return {
      yearOfBirth,
      monthOfBirth,
      dayOfBirth
    };
  };

  const dateOfBirth = personInfo?.date_of_birth?.split('/') ?? '';
  const dateOfBirthObject = getDateOfBirth(personInfo.date_of_birth);
  
  const arrayOfYears = getArrayOfYears();
  const { first_name: firstName, last_name: lastName } = personInfo;

  const validateName = (value) => {
    if (!apostropheRegExp.test(value[0])) {
      return 'The field cannot begin with an apostrophe, space/blank, hyphen, or period';
    }
    if (value.length > 50) {
      return 'The field cannot be greater than 50 characters';
    }
    return '';
  };

  const {
    formState: { errors, isValid },
    control,
    handleSubmit,
    watch,
    getValues,
    trigger,
    setFocus,
    reset,
  } = useForm({
    defaultValues : {
      firstName,
      lastName,
      email        : auth0User?.email,
      home_phone   : personInfo?.home_phone,
      phone        : personInfo?.phone,
      monthOfBirth : dateOfBirthObject?.monthOfBirth,
      dayOfBirth   : dateOfBirthObject?.dayOfBirth,
      yearOfBirth  : dateOfBirthObject?.yearOfBirth,
      address      : personInfo?.address,
      city         : personInfo?.city,
      state        : personInfo?.state,
      postal_code  : personInfo?.postal_code,
      gender       : personInfo?.gender,
    },
  });

  const { monthOfBirth, yearOfBirth, postal_code, phone } = getValues();

  // Handlers
  const onSubmit = handleSubmit(async (data) => {
    const { yearOfBirth, monthOfBirth, dayOfBirth } = data;
    const dateOfBirth = `${yearOfBirth}/${monthOfBirth}/${dayOfBirth}`;
    const parseData = {
      ...data,
      date_of_birth : dateOfBirth,
    };
    delete parseData.yearOfBirth;
    delete parseData.monthOfBirth;
    delete parseData.dayOfBirth;
    delete parseData.email;
    const attributes = getPersonAttribute(
      person?.attributes,
      initialContactFormValues
    );
    const dataAttribute = getAttributesPayload(parseData, {
      ...attributes,
      date_of_birth : '',
    });
    const formattedAttribute = formatPhoneNumbers(dataAttribute);
    const body = formatAttributeName(formattedAttribute, [
      { old: 'firstName', new: 'first_name' },
      { old: 'lastName', new: 'last_name' },
    ]);

    if (body.length > 0) {
      // upsert attributes
      const { error } = await upsertPersonAttribute({
        body,
        personId,
      });
      if (error) {
        return dispatch(
          openSnackbar({
            type : 'error',
            message :
              'Could not save your personal information. Please try again',
          })
        );
      }
    }
    hasOptedInRef.current = Boolean(data.phone);
    if (isPulseSurveyOpenModal) {
      handleIsOptingIn();
    }
    dispatch(
      openSnackbar({
        type    : 'success',
        message : 'Your personal information have been successfully saved',
      })
    );
    handleClose();
  });

  useEffect(() => {
    if (!phone && isPulseSurveyOpenModal) {
      setTimeout(() => {
        const phoneInput = document.getElementById('phone');
        if (phoneInput) phoneInput.focus();
        setFocus('phone');
      }, 500);
    }
  }, [phone, isPulseSurveyOpenModal]);

  useEffect(() => {
    const data = {
      firstName,
      lastName,
      email        : auth0User?.email,
      phone        : formatNationalPhoneNumber(personInfo?.phone),
      home_phone   : formatNationalPhoneNumber(personInfo?.home_phone),
      monthOfBirth : dateOfBirthObject?.monthOfBirth,
      dayOfBirth   : dateOfBirthObject?.dayOfBirth,
      yearOfBirth  : dateOfBirthObject?.yearOfBirth,
      address      : personInfo?.address,
      city         : personInfo?.city,
      state        : personInfo?.state,
      postal_code  : personInfo?.postal_code,
      gender       : personInfo?.gender,
    };
    reset(data);
  }, [isPersonLoading]);

  return (
    <PKDModal
      isOpen={isOpenModal}
      onClose={handleClose}
      title='Personal information'
      maxWidth={{ xl: '800px' }}>
      <form
        onSubmit={handleSubmit(onSubmit)}>
        <FormRow
          marginTop='24px' gridGap='16px'>
          <Box>
            <Controller
              control={control}
              name='firstName'
              rules={{
                required : {
                  value   : true,
                  message : 'Please enter your first name',
                },
                pattern : {
                  value   : nameRegExp,
                  message : 'Name can only contain string values',
                },
              }}
              render={({ field: { onChange } }) => (
                <ErrorWrapper
                  message={errors.firstName?.message}>
                  <TextInput
                    fullWidth
                    value={watch('firstName')}
                    onChange={(e) => {
                      onChange(e);
                      trigger(allFields);
                    }}
                    label='First name'
                    error={errors.firstName}
                    hasErrors={
                      errors.firstName && {
                        type    : 'custom',
                        message : errors.firstName?.message,
                      }
                    }
                  />
                </ErrorWrapper>
              )}
            />
          </Box>
          <Box>
            <Controller
              control={control}
              name='lastName'
              rules={{
                required : {
                  value   : true,
                  message : 'Please enter your first name',
                },
                validate : (value) => validateName(value) === '',
                pattern  : {
                  value   : nameRegExp,
                  message : 'Name can only contain string values',
                },
              }}
              render={({ field: { onChange, value } }) => (
                <ErrorWrapper
                  message={errors.lastName?.message}>
                  <TextInput
                    fullWidth
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      trigger(allFields);
                    }}
                    label='Last name'
                    error={errors.lastName}
                    hasErrors={
                      errors.lastName && {
                        type    : 'custom',
                        message : errors.lastName?.message,
                      }
                    }
                  />
                </ErrorWrapper>
              )}
            />
          </Box>
        </FormRow>
        <FormRow
          marginTop='24px' gridGap='16px' cols='1'>
          <Box>
            <Controller
              control={control}
              name='email'
              render={({ field: { onChange } }) => (
                <ErrorWrapper
                  message={errors.email}>
                  <TextInput
                    fullWidth
                    value={watch('email')}
                    onChange={(e) => {
                      onChange(e);
                      trigger(allFields);
                    }}
                    disabled
                    label='Email'
                    error={!!errors?.email?.message}
                    hasErrors={
                      errors.email && {
                        type    : 'custom',
                        message : errors.email.message,
                      }
                    }
                  />
                </ErrorWrapper>
              )}
            />
          </Box>
        </FormRow>
        <FormRow
          marginTop='24px' gridGap='16px'>
          <Box>
            <Controller
              control={control}
              name='phone'
              rules={{
                pattern : {
                  value : phoneNumberFormatRegExp,
                  message :
                    'Invalid phone number. Should be (XXX) XXX-XXXX or XXX XXX-XXXX',
                },
              }}
              render={({ field: { onChange }, fieldState: { error } }) => (
                <ErrorWrapper
                  message={error?.message}>
                  <TextInput
                    value={watch('phone')}
                    onChange={(e) => {
                      onChange(e);
                      trigger(allFields);
                    }}
                    inputRef={(input) => input && input.focus()}
                    id='phone'
                    label='Mobile Phone (optional)'
                    fullWidth
                    inputProps={{
                      mask        : '(#00) 000-0000',
                      definitions : {
                        '#' : /[1-9]/,
                      },
                    }}
                    InputProps={{
                      inputComponent : MaskedInput,
                    }}
                    error={Boolean(error?.message)}
                    hasErrors={
                      errors.phone && {
                        type    : 'custom',
                        message : error.message,
                      }
                    }
                  />
                </ErrorWrapper>
              )}
            />
          </Box>
          <Box>
            <Controller
              control={control}
              name='home_phone'
              rules={{
                pattern : {
                  value : phoneNumberFormatRegExp,
                  message :
                    'Invalid phone number. Should be (XXX) XXX-XXXX or XXX XXX-XXXX',
                },
              }}
              render={({ field: { value, onChange } }) => (
                <ErrorWrapper
                  message={errors.home_phone}>
                  <TextInput
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      trigger(allFields);
                    }}
                    label='Home Phone (optional)'
                    fullWidth
                    inputProps={{
                      mask        : '(#00) 000-0000',
                      definitions : {
                        '#' : /[1-9]/,
                      },
                    }}
                    InputProps={{
                      inputComponent : MaskedInput,
                    }}
                    error={!!errors?.home_phone?.message}
                    hasErrors={
                      errors.home_phone && {
                        type    : 'custom',
                        message : errors.home_phone.message,
                      }
                    }
                  />
                </ErrorWrapper>
              )}
            />
          </Box>
        </FormRow>
        <FormRow
          marginTop='24px' gridGap='16px' cols='3'>
          <Box>
            <Controller
              control={control}
              name='yearOfBirth'
              rules={{
                required : {
                  value   : true,
                  message : 'This field is required',
                },
              }}
              render={({ field: { onChange } }) => (
                <ErrorWrapper>
                  <PKDSelect
                    id={`${getRandomIndex()}`}
                    onChange={(e) => {
                      onChange(e);
                      trigger(allFields);
                    }}
                    error={!!errors?.yearOfBirth?.message}
                    hasErrors={
                      errors.yearOfBirth && {
                        type    : 'custom',
                        message : errors.yearOfBirth.message,
                      }
                    }
                    value={watch('yearOfBirth')}
                    label='YYYY'
                    options={arrayOfYears.map((year) => ({
                      label : year,
                      value : `${year}`,
                    }))}
                  />
                </ErrorWrapper>
              )}
            />
          </Box>
          <Box>
            <Controller
              control={control}
              name='monthOfBirth'
              rules={{
                required : {
                  value   : true,
                  message : 'This field is required',
                },
              }}
              render={({ field: { onChange } }) => (
                <ErrorWrapper>
                  <PKDSelect
                    id={`${getRandomIndex()}`}
                    label='MM'
                    value={watch('monthOfBirth')}
                    onChange={(e) => {
                      onChange(e);
                      trigger(allFields);
                    }}
                    error={!!errors?.monthOfBirth?.message}
                    hasErrors={
                      errors.monthOfBirth && {
                        type    : 'custom',
                        message : errors.monthOfBirth.message,
                      }
                    }
                    options={Array.from({ length: 12 }).map((_, index) => ({
                      label : `${index + 1}`.padStart(2, '0'),
                      value : `${index + 1}`.padStart(2, '0'),
                    }))}
                  />
                </ErrorWrapper>
              )}
            />
          </Box>
          <Box>
            <Controller
              control={control}
              name='dayOfBirth'
              rules={{
                required : {
                  value   : true,
                  message : 'This field is required',
                },
              }}
              render={({ field: { value, onChange } }) => (
                <ErrorWrapper>
                  <PKDSelect
                    id={`${getRandomIndex()}`}
                    onChange={(e) => {
                      onChange(e);
                      trigger(allFields);
                    }}
                    value={value}
                    error={!!errors?.dayOfBirth?.message}
                    hasErrors={
                      errors.dayOfBirth && {
                        type    : 'custom',
                        message : errors.dayOfBirth.message,
                      }
                    }
                    label='DD'
                    options={getDaysOfMonth(monthOfBirth, yearOfBirth)}
                  />
                </ErrorWrapper>
              )}
            />
          </Box>
        </FormRow>
        <FormRow
          marginTop='24px' gridGap='16px' cols='1'>
          <Box>
            <Controller
              control={control}
              name='address'
              rules={{
                maxLength : {
                  value   : 50,
                  message : 'Maximum characters allowed is 50',
                },
                pattern : {
                  value   : addressFormatRegExp,
                  message : 'You have invalid characters in your address',
                },
              }}
              render={({ field: { value, onChange } }) => (
                <ErrorWrapper
                  message={errors.address?.message}>
                  <TextInput
                    onChange={(e) => {
                      onChange(e);
                      trigger(allFields);
                    }}
                    value={value}
                    inputProps={{
                      maxLength : 50,
                    }}
                    label='Address (optional)'
                    fullWidth
                    error={!!errors?.address?.message}
                    hasErrors={
                      errors.address && {
                        type    : 'custom',
                        message : errors.address.message,
                      }
                    }
                  />
                </ErrorWrapper>
              )}
            />
          </Box>
        </FormRow>
        <FormRow
          marginTop='24px' gridGap='16px'>
          <Box>
            <Controller
              control={control}
              name='city'
              rules={{
                required : {
                  value   : true,
                  message : 'This field is required',
                },
                maxLength : {
                  value   : 50,
                  message : 'Only a maximum of 50 characters are allowed.',
                },
              }}
              render={({ field: { onChange, ...field } }) => (
                <ErrorWrapper
                  message={errors.city}>
                  <TextInput
                    {...field}
                    onChange={(e) => {
                      onChange(e);
                      trigger(allFields);
                    }}
                    value={watch('city')}
                    label='City'
                    fullWidth
                    error={!!errors?.city?.message}
                    hasErrors={
                      errors.city && {
                        type    : 'custom',
                        message : errors.city.message,
                      }
                    }
                  />
                </ErrorWrapper>
              )}
            />
          </Box>
          <Box>
            <Controller
              control={control}
              name='state'
              rules={{
                required : {
                  value   : true,
                  message : 'Please select a state',
                },
              }}
              render={({ field: { value, onChange } }) => (
                <ErrorWrapper
                  message={errors.state}>
                  <PKDSelect
                    fullWidth
                    label='State'
                    value={watch('state')}
                    onChange={(e) => {
                      onChange(e);
                      trigger(allFields);
                    }}
                    options={states?.map((state) => ({
                      label : state.name,
                      value : state.code,
                    }))}
                    error={!!errors?.state?.message}
                    hasErrors={
                      errors.state && {
                        type    : 'custom',
                        message : errors.state.message,
                      }
                    }
                  />
                </ErrorWrapper>
              )}
            />
          </Box>
        </FormRow>
        <FormRow
          marginTop='24px' cols='2'>
          <Box>
            <Controller
              control={control}
              name='postal_code'
              rules={{
                required : {
                  value   : true,
                  message : 'This field is required',
                },
                pattern : {
                  value   : zipCodeRegExp,
                  message : 'Invalid format. Should be XXXXX or or XXXXX-XXXX',
                },
              }}
              render={({ field: { onChange, ...field } }) => (
                <ErrorWrapper
                  manualMessage={errors?.postal_code?.message}>
                  <TextInput
                    {...field}
                    onChange={(e) => {
                      onChange(e);
                      trigger(allFields);
                    }}
                    value={watch('postal_code')}
                    label='Zip Code'
                    fullWidth
                    inputProps={{
                      mask        : `#0000${postal_code?.length > 4 ? '-0000' : ''}`,
                      definitions : {
                        '#' : /[0-9]/,
                      },
                    }}
                    InputProps={{
                      inputComponent : MaskedInput,
                    }}
                    error={!!errors?.postal_code?.message}
                    hasErrors={!!errors?.postal_code?.message}
                  />
                </ErrorWrapper>
              )}
            />
          </Box>
          <Box>
            <Controller
              control={control}
              name='gender'
              render={({ field: { onChange, value, ...field } }) => (
                <ErrorWrapper
                  manualMessage={errors?.gender?.message}>
                  <PKDSelect
                    id={`${getRandomIndex()}`}
                    {...field}
                    label='Gender'
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      trigger(allFields);
                    }}
                    error={!!errors?.gender?.message}
                    hasErrors={
                      errors.gender && {
                        type    : 'custom',
                        message : errors.gender.message,
                      }
                    }
                    options={Object.keys(Gender).map((gender) => ({
                      label : gender,
                      value : Gender[gender],
                    }))}
                  />
                </ErrorWrapper>
              )}
            />
          </Box>
        </FormRow>
        <Box
          marginTop='32px'
          marginBottom='25px'
          gap={2}
          display='flex'
          justifyContent='center'>
          <PKDFilledButton
            sx={{
              width : '180px',
            }}
            disabled={!isValid || isPersonAttributeUpserting || isButtonLoading}
            onClick={onSubmit}
            isLoading={isPersonAttributeUpserting || isButtonLoading}>
            Save
          </PKDFilledButton>
          <PKDFilledButton
            sx={{
              width           : '180px',
              backgroundColor : theme.palette.primary.grey3,
              color           : theme.palette.primary.gray7,
            }}
            onClick={handleClose}>
            Cancel
          </PKDFilledButton>
        </Box>
      </form>
    </PKDModal>
  );
};

export default PersonInformationModal;
